import { Logger } from '@quickplay/foundation';
import { MediaType } from '@quickplay/player-interface';
import { getMimeType } from './ShakaUtils';

/**
 * ShakaPreloadManager - Handles preloading of Shaka player content
 *
 * This class encapsulates the preloading functionality for Shaka Player,
 * allowing content to be preloaded before attaching to a video element.
 */
export class ShakaPreloadManager {
  private preloadedAsset: any = null;

  /**
   * Creates a new ShakaPreloadManager instance
   *
   * @param shakaPlayer - Existing Shaka Player instance to use for preloading
   * @param logger - Logger instance for logging
   * @param contentUrl - URL of the content to preload
   * @param mediaType - Type of media being preloaded (optional)
   * @param initialPlaybackTime - Initial playback time to start from
   */
  constructor(
    private shakaPlayer: any,
    private logger: Logger,
    private contentUrl: string,
    private mediaType?: MediaType,
    private initialPlaybackTime?: number,
  ) {}

  /**
   * Preloads the asset using the provided Shaka Player instance
   *
   * @returns Promise that resolves with the preloaded asset or rejects with an error
   */
  public async preloadAsset(): Promise<any> {
    this.logger.log('Starting preload asset creation for:', this.contentUrl);

    try {
      // Perform the actual preload using the Shaka Player instance
      this.preloadedAsset = await this.shakaPlayer.preload(
        this.contentUrl,
        this.initialPlaybackTime,
        getMimeType(this.mediaType),
      );

      this.logger.log('Preload asset created successfully');
      return this.preloadedAsset;
    } catch (error) {
      this.logger.error('Error creating preload asset:', error);
      this.preloadedAsset = null;
      throw error;
    }
  }

  /**
   * Gets the preloaded asset if available
   *
   * @returns The preloaded asset or null if not available
   */
  public getPreloadedAsset(): any {
    return this.preloadedAsset;
  }

  /**
   * Sets the preloaded asset
   *
   * @param asset - The asset to set as the preloaded asset
   */
  public setPreloadedAsset(asset: any): void {
    this.logger.debug('Setting preloaded asset for the midroll playback smart tv');
    this.preloadedAsset = asset;
  }

  /**
   * Checks if a preloaded asset is available
   *
   * @returns True if a preloaded asset is available, false otherwise
   */
  public hasPreloadedAsset(): boolean {
    return this.preloadedAsset !== null;
  }

  /**
   * Clears the preloaded asset
   */
  public clearPreloadedAsset(): void {
    this.preloadedAsset = null;
  }
}
